import React from 'react';
import { useTranslation } from 'react-i18next';
import { Control, UseFormHandleSubmit, UseFormSetValue } from 'react-hook-form';

import { Icon } from '@common/components/icon';
import { Modal } from '@common/components/modal';
import { Button } from '@common/components/button';
import { FormIcon } from '@modules/forms/components/form-icon';
import Label from '@common/components/form/label';
import { Group, Row } from '@common/components/form';

import type { EvaluationFormPayload } from '@modules/performance/types';
import { useParams } from 'react-router-dom';
import { TextInputHookForm } from '@common/components/form/inputs/text';
import { APIEvaluationForm } from '@modules/performance/types';
import './form.scss';

const availableColors = [
  '#007AFF',
  '#3C9EEF',
  '#33B29C',
  '#42B35D',
  '#F6AE35',
  '#FF9500',
  '#FCD34D',
  '#F78C36',
  '#F76E63',
  '#F77995',
  '#F57ECB',
  '#9E74FA',
  '#5856D6',
  '#AF52DE',
];

enum EFormIconPaths {
  ACCOUNT_CIRCLE = 'default/forms/account-circle.png',
  ACCOUNT_TREE = 'default/forms/account-tree.png',
  ANALYTICS = 'default/forms/analytics.png',
  ASSIGNMENT = 'default/forms/assignment.png',
  BUSINESS = 'default/forms/business.png',
  CAKE = 'default/forms/cake.png',
  CALENDAR_TODAY = 'default/forms/calendar-today.png',
  CLOTHES = 'default/forms/clothes.png',
  COFFEE = 'default/forms/coffee.png',
  EMAIL = 'default/forms/email.png',
  FAVORITE = 'default/forms/favorite.png',
  FLIGHT = 'default/forms/flight.png',
  FOLDER = 'default/forms/folder.png',
  LOCK = 'default/forms/lock.png',
  MEDICAL_CROSS = 'default/forms/medical-cross.png',
  MONEY = 'default/forms/money.png',
  SCHOOL = 'default/forms/school.png',
  SETTINGS = 'default/forms/settings.png',
  SHIELD = 'default/forms/shield.png',
  SIGNATURE = 'default/forms/signature.png',
}

type Props = {
  children?: React.ReactNode;
  submitting: boolean;
  values: APIEvaluationForm | Record<string, any>;
  control?: Control<APIEvaluationForm>;
  setValue: UseFormSetValue<APIEvaluationForm>;
  onSubmit: UseFormHandleSubmit<APIEvaluationForm> | (() => Promise<void>);
  onClose?: () => void;
};

type Params = {
  id?: string;
};

const FormFormUnconnected = ({
  children,
  values,
  submitting = false,
  control,
  setValue,
  onClose,
  onSubmit,
}: Props) => {
  const { t } = useTranslation();
  const params = useParams<Params>();
  const isEditForm = !!params.id;

  const handleSubmit = (data: EvaluationFormPayload) => {
    if (typeof onSubmit === 'function') {
      onSubmit(data as any);
    }
  };

  return (
    <Modal
      list
      size="small"
      title={isEditForm ? t('performance:update_form') : t('performance:create_form')}
      onClose={onClose}
      content={
        <Group>
          <Label text={t('performance:form_form_title')} />
          <Row>
            <FormIcon form={values} />
            <TextInputHookForm
              name="title"
              placeholder={t('performance:form_form_title_placeholder')}
              control={control as Control<APIEvaluationForm>}
            />
          </Row>
          <Row>
            <Group>
              <Label text={t('performance:form_form_color')} />
              <div className="Input__ColorPicker">
                {availableColors.map((hex) => (
                  <div
                    key={hex}
                    role="button"
                    style={{ backgroundColor: hex }}
                    onClick={() => {
                      setValue('color', hex);
                    }}
                  >
                    {values.color === hex && <Icon type="check" />}
                  </div>
                ))}
              </div>
            </Group>
          </Row>
          <Row>
            <Group>
              <Label text={t('performance:form_form_icon')} />
              <div className="Input__IconPicker">
                {Object.values(EFormIconPaths).map((path) => (
                  <div
                    key={path}
                    className={values.icon === path ? 'active' : undefined}
                    onClick={() => {
                      setValue('icon', path);
                    }}
                    role="button"
                  >
                    <img
                      src={`https://assets.oneteam.io/globals/attachments/${path.replace(
                        '.png',
                        '-preview.png'
                      )}`}
                      alt="Icon"
                    />
                  </div>
                ))}
              </div>
            </Group>
          </Row>
        </Group>
      }
      footer={
        !!onClose && (
          <Button
            type="primary"
            buttonType="submit"
            onClick={() => handleSubmit(values as EvaluationFormPayload)}
            isLoading={submitting}
          >
            {t('performance:create')}
          </Button>
        )
      }
    >
      {children}
    </Modal>
  );
};

export const FormForm = FormFormUnconnected;
